import { useMemo } from "react";
import { useACLContext } from "../context";
import type { Permission, RoleName, Scope } from "@shield-acl/core";

export interface UserPermissions {
  /** Efetivas no scope (roles resolvidas + diretas). */
  all: Permission[];
  /** Diretas nos grants deste scope (correspondência exata). */
  direct: Permission[];
  byRole: Record<RoleName, Permission[]>;
  denials: Permission[];
  allowances: Permission[];
  actions: string[];
  resources: string[];
  count: number;
}

const EMPTY: UserPermissions = {
  all: [],
  direct: [],
  byRole: {},
  denials: [],
  allowances: [],
  actions: [],
  resources: [],
  count: 0,
};

/** Permissões efetivas do usuário no scope — para telas de admin/debug. */
export function usePermissions(opts?: { scope?: Scope }): UserPermissions {
  const { engine, user, scope, revision } = useACLContext();
  const active = opts?.scope ?? scope;

  return useMemo(() => {
    if (!user) return EMPTY;

    const all = engine.getUserPermissions(user, active);
    const direct = user.grants
      .filter((g) => g.scope === active)
      .flatMap((g) => g.permissions ?? []);

    const byRole: Record<RoleName, Permission[]> = {};
    engine.getGrantedRoles(user, active).forEach((roleName) => {
      const role = engine.getRole(roleName);
      if (role) byRole[roleName] = role.permissions;
    });

    const actions = new Set<string>();
    const resources = new Set<string>();
    for (const p of all) {
      (Array.isArray(p.action) ? p.action : [p.action]).forEach((a) =>
        actions.add(a),
      );
      if (p.resource !== undefined) {
        (Array.isArray(p.resource) ? p.resource : [p.resource]).forEach((r) =>
          resources.add(r),
        );
      }
    }

    return {
      all,
      direct,
      byRole,
      denials: all.filter((p) => p.deny === true),
      allowances: all.filter((p) => p.deny !== true),
      actions: [...actions].sort(),
      resources: [...resources].sort(),
      count: all.length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, user, active, revision]);
}
